import { Injectable } from '@angular/core';
import { PortalProductsStore } from './portal-products.store';
import { PortalProductsQuery } from './portal-products.query';
import { ApiService } from '../../shared/services/core/api.service';
import { LoggingService } from '../../shared/services/core/logging.service';
import { Observable, of } from 'rxjs';
import { PortalProduct } from './models/portal-product.model';
import { tap, mapTo } from 'rxjs/operators';
import { MemberProduct } from './models/member-product.model';
import { ProviderUiActionResponse } from '../../ecash/models/provider-ui-action-response.model';
import { ProductOrder } from './models/product-order';
import { CallBackModel } from '../../ecash/models/callback.model';
import { Address } from '../../account/address/models/address.model';
import { config } from '../../shared/config';
import { GiftCodeModel } from '../../purchase/models/giftcode/gift-code.model';
import { ProductBasketAdditionalCharges } from './models/product-basket-additional-charges.model';


@Injectable({ providedIn: 'root' })
export class PortalProductsService {
  constructor(
    private portalProductsStore: PortalProductsStore,
    private portalProductsQuery: PortalProductsQuery,
    private api: ApiService,
    private logService: LoggingService
  ) {}

  public fetchPortalProducts() {
    this.portalProductsStore.setLoading(true);
    this.logService.info('making call for portal products');

    const request$: Observable<null> = this.api.get<PortalProduct>('portalproducts').pipe(
      tap((products) => this.portalProductsStore.set(products)),
      mapTo(null)
    );

    return this.portalProductsQuery.getHasCache() ? of(null) : request$;
  }

  public calculateAdditionalCharges(memberProducts: MemberProduct[],  useMemberDetailsAddress: boolean = false, appliedGiftCodes: GiftCodeModel[] = []) {
    this.logService.info('calculating additional charges');
    let queryString = "";

    memberProducts.forEach((product) => {
      queryString += `productids=${product.ProductId}`;
      if ((memberProducts.indexOf(product) < memberProducts.length - 1) || appliedGiftCodes.length > 0) {
        queryString += "&";
      }
    });

    appliedGiftCodes.forEach((giftCode) => {
      queryString += `appliedgiftcodeids=${giftCode.Id}`;
      if (appliedGiftCodes.indexOf(giftCode) < appliedGiftCodes.length - 1) {
        queryString += "&";
      }
    });

    return this.api.getAnySingle<ProductBasketAdditionalCharges>(`portalproducts/additionalcharges?${queryString}&useMemberDetailsAddress=${useMemberDetailsAddress}`);
  }

  public purchase(
    memberProducts: MemberProduct[],
    address: Address,
    appliedGiftCodes?: GiftCodeModel[],
    resultUrl = '/portalproducts/result',
    useMemberDetailsAddress: boolean = false,
    useSubmittedAddress: boolean = false
  ): Observable<ProviderUiActionResponse> {
    let order: ProductOrder = {
      ResultUrl: config.portalUrl + resultUrl,
      MemberProducts: memberProducts,
      Address: address,
      UseMemberDetailsAddress: useMemberDetailsAddress,
      UseSubmittedAddress: useSubmittedAddress,
      GiftCodes: appliedGiftCodes,
    };

    this.logService.debug('Making request for purchases', order);

    return this.api.post('portalproducts/createorder', order);
  }

  public callBack(callback: CallBackModel): Observable<ProviderUiActionResponse> {
    this.logService.debug('Portal Product callback received', callback);
    return this.api.post<CallBackModel>('portalproducts/result', callback);
  }
}
